'use client'

import { useEffect, useState } from 'react'
import { deserializeFloorplan, type Floorplan } from '@ideight/engine/model'
import type { AssetMeshSource } from '@ideight/engine/react'
import { HDB_PRESETS, loadPreset, type HdbPresetKey } from '@ideight/hdb-presets'
import type { Asset, AssetCategory } from '@ideight/api-client'
import { api } from '../../lib/api'
import { SampleScene } from '../_components/three/SampleScene'

export interface PreviewClientProps {
  readonly presetKey: HdbPresetKey
}

/**
 * Fallback furniture (by catalogue category) for when no admin sample is
 * published yet — dropped into the largest room of the preset so the demo
 * is never an empty shell. Missing categories are skipped.
 */
const DEMO_PICKS: readonly { category: AssetCategory; dx: number; dy: number; rot: number }[] = [
  { category: 'sofa', dx: 0, dy: 600, rot: 0 },
  { category: 'table', dx: 0, dy: -150, rot: 0 },
  { category: 'chair', dx: 800, dy: 250, rot: -Math.PI / 2 },
  { category: 'lighting', dx: -750, dy: -350, rot: 0 },
]

export function PreviewClient({ presetKey }: PreviewClientProps) {
  const [assetsById, setAssetsById] = useState<ReadonlyMap<string, AssetMeshSource>>(new Map())
  const [floorplan, setFloorplan] = useState<Floorplan | null>(null)
  const [label, setLabel] = useState<string>(HDB_PRESETS[presetKey].label)

  useEffect(() => {
    let cancelled = false
    async function load(): Promise<void> {
      // Public catalogue → mesh source for whatever furniture is placed.
      let ready: readonly Asset[] = []
      try {
        ready = (await api.listAssets({ status: 'ready' })).assets.filter((a) => a.gltfUrl !== null)
      } catch {
        ready = []
      }
      if (cancelled) return
      setAssetsById(
        new Map(ready.map((a) => [a.id, { id: a.id, gltfUrl: a.gltfUrl, status: a.status }])),
      )

      // Prefer the admin-curated featured sample; fall back to an
      // auto-furnished preset when none is published.
      let fp: Floorplan | null = null
      try {
        const samples = await api.listSamples()
        const featured = samples.find((s) => s.featured) ?? samples[0]
        if (featured) {
          const data = await api.getSampleFloorplan(featured.id)
          if (data) {
            fp = deserializeFloorplan(data)
            if (!cancelled) setLabel(featured.name)
          }
        }
      } catch {
        fp = null
      }
      if (cancelled) return
      if (!fp) {
        fp = loadPreset(presetKey)
        if (ready.length > 0) furnishDemo(fp, ready)
      }
      setFloorplan(fp)
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [presetKey])

  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: 12,
          left: 16,
          zIndex: 10,
          color: '#e5e5e5',
          font: '13px -apple-system, system-ui, sans-serif',
          background: 'rgba(20,20,20,0.7)',
          padding: '8px 12px',
          borderRadius: 4,
        }}
      >
        <strong>{label}</strong>
        <br />
        <span style={{ color: '#999' }}>Live 3D demo · drag to look around</span>
      </div>
      {floorplan ? (
        <SampleScene floorplan={floorplan} assetsById={assetsById} />
      ) : (
        <div style={{ padding: 40, color: '#888' }}>Loading the demo…</div>
      )}
    </>
  )
}

/** Drop the fallback furniture into the floorplan's largest room. */
function furnishDemo(fp: Floorplan, ready: readonly Asset[]): void {
  const centre = largestRoomCentreMm(fp)
  if (!centre) return
  for (const pick of DEMO_PICKS) {
    const asset = ready.find((a) => a.category === pick.category)
    if (!asset) continue
    fp.addSceneItem({
      id: `demo-${pick.category}`,
      assetId: asset.id,
      xMm: centre.x + pick.dx,
      yMm: centre.y + pick.dy,
      zMm: 0,
      rotationY: pick.rot,
      dimsMm: asset.dims,
    })
  }
}

/** Bounding-box centre (mm) of the floorplan's largest interior room. */
function largestRoomCentreMm(fp: Floorplan): { x: number; y: number } | null {
  let best: { x: number; y: number } | null = null
  let bestArea = 0
  for (const cornerIds of fp.findRooms()) {
    const pts = cornerIds
      .map((id) => fp.getCorner(id))
      .filter((c): c is NonNullable<typeof c> => c != null)
    if (pts.length < 3) continue
    const xs = pts.map((p) => p.x)
    const ys = pts.map((p) => p.y)
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minY = Math.min(...ys)
    const maxY = Math.max(...ys)
    const area = (maxX - minX) * (maxY - minY)
    if (area > bestArea) {
      bestArea = area
      best = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 }
    }
  }
  return best
}
